import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

import {
  MdDashboard,
  MdApi,
  MdStorage,
  MdPlaylistAddCheck,
  MdViewList,
  MdAssessment,
  MdCalendarToday,
  MdNotificationsNone,
  MdAccountCircle,
  MdArrowDropDown,
  MdEdit,
  MdCode,
  MdExtension,
  MdHelpOutline,
  MdContactMail,
  MdLogout,
  MdFileUpload,
  MdExplore
} from 'react-icons/md';

import './App.css';

import Dashboard from './pages/Dashboard';
import APIManagement from './pages/APIManagement';
import CSVUpload from './pages/CSVUpload';
import TestSteps from './pages/TestSteps';
import TestSuiteBuilder from './pages/TestSuiteBuilder';
import ExecutionResults from './pages/ExecutionResults';
import CustomFunctions from './pages/CustomFunctions';
import DefaultExtensions from './pages/DefaultExtensions';
import About from './pages/About';
import Contact from './pages/Contact';
import AuthPage from './pages/AuthPage';
import FileAssets from './pages/FileAssets';
import Addons from './pages/Addons';
import PublicSite from './pages/PublicSite';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000').replace(/\/$/, '');
axios.defaults.baseURL = API_BASE_URL;

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: MdDashboard },
  { key: 'apis', label: 'API Collection', icon: MdApi },
  { key: 'csv', label: 'Test Data', icon: MdStorage },
  { key: 'files', label: 'Files Asset', icon: MdFileUpload },
  { key: 'teststeps', label: 'Test Steps', icon: MdPlaylistAddCheck },
  { key: 'suites', label: 'Test Suites', icon: MdViewList },
  { key: 'results', label: 'Results', icon: MdAssessment },
  { key: 'customFunctions', label: 'Add Custom Function', icon: MdCode },
  { key: 'extensions', label: 'Default Extensions', icon: MdExtension },
  { key: 'addons', label: 'Automation Addons', icon: MdExplore },
  { key: 'about', label: 'About Tool', icon: MdHelpOutline },
  { key: 'contact', label: 'Contact Us', icon: MdContactMail }
];

const PAGE_TITLES = {
  dashboard: 'Automation Dashboard',
  apis: 'API Collection',
  csv: 'Test Data Manager',
  teststeps: 'Test Steps',
  customFunctions: 'Add Custom Function',
  extensions: 'Default Extensions',
  suites: 'Test Suite Builder',
  results: 'Execution Results',
  about: 'About KREAODEZ AUTOMATION',
  contact: 'Contact Us',
  files: 'File Assets Manager',
  addons: 'Automation Addons'
};

const AUTH_STORAGE_KEY = 'automation.authSession';
const MAX_NOTIFICATIONS = 100;
const DEFAULT_PAGE = 'dashboard';
const DEFAULT_PUBLIC_PAGE = 'home';
const PAGE_ROUTES = {
  dashboard: '/dashboard',
  apis: '/api-collection',
  csv: '/test-data',
  files: '/files',
  teststeps: '/testcase',
  suites: '/test-suites',
  results: '/results',
  customFunctions: '/custom-functions',
  extensions: '/default-extensions',
  addons: '/addons',
  about: '/about',
  contact: '/contact'
};
const PUBLIC_PAGE_ROUTES = {
  home: '/',
  about: '/about',
  contact: '/contact',
  faq: '/faq',
  privacy: '/privacy-policy',
  terms: '/terms-of-use',
  login: '/login'
};
const ROUTE_TO_PAGE = Object.entries(PAGE_ROUTES).reduce((acc, [page, route]) => {
  acc[route] = page;
  return acc;
}, {});
const ROUTE_TO_PUBLIC_PAGE = Object.entries(PUBLIC_PAGE_ROUTES).reduce((acc, [page, route]) => {
  acc[route] = page;
  return acc;
}, {});
const KNOWN_APP_ROUTES = new Set(Object.values(PAGE_ROUTES));

const getNotificationStorageKey = (userId) => `automation.executionNotifications.${String(userId || 'guest')}`;
const getAuthStorage = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  return window.sessionStorage;
};
const getRedirectPathFromQuery = () => {
  if (typeof window === 'undefined') return '';

  const params = new URLSearchParams(window.location.search);
  return params.get('redirect') || '';
};
const normalizePathname = (pathname) => {
  if (!pathname || pathname === '/') return '/';
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
};
const getPageFromPath = (pathname) => ROUTE_TO_PAGE[normalizePathname(pathname)] || DEFAULT_PAGE;
const getPathForPage = (page) => PAGE_ROUTES[page] || PAGE_ROUTES[DEFAULT_PAGE];
const getPublicPageFromPath = (pathname) => {
  const normalized = normalizePathname(pathname);
  if (ROUTE_TO_PUBLIC_PAGE[normalized]) return ROUTE_TO_PUBLIC_PAGE[normalized];
  if (KNOWN_APP_ROUTES.has(normalized)) return 'login';
  return DEFAULT_PUBLIC_PAGE;
};
const getPublicPathForPage = (page) => PUBLIC_PAGE_ROUTES[page] || PUBLIC_PAGE_ROUTES[DEFAULT_PUBLIC_PAGE];
const applyAuthTokenToAxios = (token) => {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    return;
  }

  delete axios.defaults.headers.common.Authorization;
};

const loadStoredAuth = () => {
  const authStorage = getAuthStorage();
  if (!authStorage) return { token: '', user: null };
  try {
    const raw = authStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return { token: '', user: null };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { token: '', user: null };
    return {
      token: String(parsed.token || ''),
      user: parsed.user || null
    };
  } catch (error) {
    console.error('Failed to load auth session:', error);
    return { token: '', user: null };
  }
};

const loadStoredNotifications = (userId) => {
  if (typeof window === 'undefined' || !window.localStorage || !userId) return [];

  try {
    const raw = window.localStorage.getItem(getNotificationStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to load notifications:', error);
    return [];
  }
};

function App() {
  const storedAuth = loadStoredAuth();
  applyAuthTokenToAxios(storedAuth.token || '');
  const redirectPath = getRedirectPathFromQuery();
  const initialPath = redirectPath || (typeof window === 'undefined' ? '/' : window.location.pathname);
  const initialPage = typeof window === 'undefined'
    ? DEFAULT_PAGE
    : getPageFromPath(initialPath);
  const initialPublicPage = typeof window === 'undefined'
    ? DEFAULT_PUBLIC_PAGE
    : getPublicPageFromPath(initialPath);

  const [authToken, setAuthToken] = useState(storedAuth.token || '');
  const [currentUser, setCurrentUser] = useState(storedAuth.user || null);

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [publicPage, setPublicPage] = useState(initialPublicPage);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [stats, setStats] = useState({ apis: 0, csvs: 0, suites: 0 });
  const [host, setHost] = useState('');
  const [openAiApiKey, setOpenAiApiKey] = useState('');

  const [notifications, setNotifications] = useState(() => loadStoredNotifications(storedAuth.user?.id));
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [focusedExecution, setFocusedExecution] = useState(null);

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: ''
  });

  const notificationPanelRef = useRef(null);
  const profilePanelRef = useRef(null);

  const clearAuthState = useCallback(() => {
    setAuthToken('');
    setCurrentUser(null);
    setCurrentPage(DEFAULT_PAGE);
    setStats({ apis: 0, csvs: 0, suites: 0 });
    setHost('');
    setOpenAiApiKey('');
    setNotifications([]);
    setIsNotificationOpen(false);
    setIsProfileOpen(false);
    setShowProfileModal(false);
    setFocusedExecution(null);
  }, []);

  const navigateToPage = useCallback((page, options = {}) => {
    const nextPage = PAGE_ROUTES[page] ? page : DEFAULT_PAGE;
    const nextPath = getPathForPage(nextPage);
    const shouldReplace = Boolean(options.replace);
    const search = options.search !== undefined
      ? options.search
      : (typeof window === 'undefined' ? '' : window.location.search);

    setCurrentPage(nextPage);

    if (typeof window === 'undefined') return;

    const currentUrl = `${window.location.pathname}${window.location.search}`;
    const nextUrl = `${nextPath}${search}`;
    if (currentUrl === nextUrl) return;

    if (shouldReplace) {
      window.history.replaceState({}, '', nextUrl);
      return;
    }

    window.history.pushState({}, '', nextUrl);
  }, []);

  const navigatePublicPage = useCallback((page, options = {}) => {
    const nextPage = PUBLIC_PAGE_ROUTES[page] ? page : DEFAULT_PUBLIC_PAGE;
    const nextPath = getPublicPathForPage(nextPage);
    const shouldReplace = Boolean(options.replace);
    const search = options.search !== undefined
      ? options.search
      : (typeof window === 'undefined' ? '' : window.location.search);

    setPublicPage(nextPage);

    if (typeof window === 'undefined') return;

    const currentUrl = `${window.location.pathname}${window.location.search}`;
    const nextUrl = `${nextPath}${search}`;
    if (currentUrl === nextUrl) return;

    if (shouldReplace) {
      window.history.replaceState({}, '', nextUrl);
      return;
    }

    window.history.pushState({}, '', nextUrl);
  }, []);

  useEffect(() => {
    if (!redirectPath || typeof window === 'undefined') return;

    const restoredPage = getPageFromPath(redirectPath);
    navigateToPage(restoredPage, { replace: true, search: '' });
  }, [navigateToPage, redirectPath]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const executionId = params.get('executionId');
    const autoDownload = params.get('autoDownload') === 'true';
    if (executionId) {
      navigateToPage('results', { replace: true });
      setFocusedExecution({
        executionId,
        autoDownload,
        triggeredAt: Date.now()
      });
      // Clear the executionId from the URL to prevent it from re-loading if the user refreshes later
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [navigateToPage]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handlePopState = () => {
      setCurrentPage(getPageFromPath(window.location.pathname));
      setPublicPage(getPublicPageFromPath(window.location.pathname));
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !authToken || !currentUser) return;

    const routePage = getPageFromPath(window.location.pathname);
    if (routePage !== currentPage) {
      navigateToPage(routePage, { replace: true });
    }
  }, [authToken, currentUser, currentPage, navigateToPage]);

  useEffect(() => {
    if (!authToken || !currentUser) {
      const authStorage = getAuthStorage();
      if (authStorage) {
        authStorage.removeItem(AUTH_STORAGE_KEY);
      }
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(AUTH_STORAGE_KEY);
      }
      return;
    }

    const authStorage = getAuthStorage();
    if (authStorage) {
      authStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ token: authToken, user: currentUser }));
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  }, [authToken, currentUser]);

  useEffect(() => {
    applyAuthTokenToAxios(authToken);
  }, [authToken]);

  useEffect(() => {
    if (!currentUser?.id) {
      setNotifications([]);
      return;
    }
    setNotifications(loadStoredNotifications(currentUser.id));
  }, [currentUser?.id]);

  useEffect(() => {
    if (!currentUser?.id || typeof window === 'undefined' || !window.localStorage) return;

    try {
      window.localStorage.setItem(
        getNotificationStorageKey(currentUser.id),
        JSON.stringify(notifications.slice(0, MAX_NOTIFICATIONS))
      );
    } catch (error) {
      console.error('Failed to save notifications:', error);
    }
  }, [notifications, currentUser?.id]);

  useEffect(() => {
    if (!authToken) return;

    let isCancelled = false;
    const syncUser = async () => {
      try {
        const response = await axios.get('/api/auth/me');
        if (!isCancelled) {
          setCurrentUser(response.data?.user || null);
        }
      } catch (error) {
        if (!isCancelled) {
          clearAuthState();
        }
      }
    };

    syncUser();
    return () => {
      isCancelled = true;
    };
  }, [authToken, clearAuthState]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401 && authToken) {
          clearAuthState();
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptorId);
  }, [authToken, clearAuthState]);

  useEffect(() => {
    if (!isNotificationOpen && !isProfileOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (notificationPanelRef.current && !notificationPanelRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
      if (profilePanelRef.current && !profilePanelRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isNotificationOpen, isProfileOpen]);

  useEffect(() => {
    setIsNotificationOpen(false);
    setIsProfileOpen(false);
  }, [currentPage]);

  const fetchConfig = useCallback(async () => {
    if (!authToken) return;
    try {
      const response = await axios.get('/api/config');
      setHost(response.data.host || '');
      setOpenAiApiKey(response.data.openAiApiKey || '');
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  }, [authToken]);

  const fetchStats = useCallback(async () => {
    if (!authToken) return;

    try {
      const [apis, csvs, suites] = await Promise.all([
        axios.get('/api/apis'),
        axios.get('/api/csv'),
        axios.get('/api/suites')
      ]);
      setStats({
        apis: apis.data.length,
        csvs: csvs.data.length,
        suites: suites.data.length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [authToken]);

  useEffect(() => {
    if (!authToken || !currentUser?.id) return;
    fetchStats();
    fetchConfig();
  }, [authToken, currentUser?.id, fetchStats, fetchConfig]);

  const handleAuthenticated = (authData) => {
    const token = String(authData?.token || '');
    const user = authData?.user || null;

    if (!token || !user) {
      clearAuthState();
      return;
    }

    setAuthToken(token);
    setCurrentUser(user);
    navigateToPage(initialPage, { replace: true });
    setProfileError('');
  };

  const handleLogout = async () => {
    try {
      if (authToken) {
        await axios.post('/api/auth/logout');
      }
    } catch (error) {
      // Best effort logout.
    } finally {
      clearAuthState();
      navigatePublicPage('login', { replace: true, search: '' });
    }
  };

  const getExecutionLabel = (execution) => {
    const executionType = String(execution?.executionType || '').trim();
    if (executionType === 'testStep') return 'Test Step';
    if (executionType === 'testGroup') return 'Test Group';
    return 'Test Suite';
  };

  const handleExecutionComplete = (execution) => {
    if (!execution?.id) return;

    const passed = Number(execution?.summary?.passed || 0);
    const total = Number(execution?.summary?.total || 0);
    const failed = Number(execution?.summary?.failed || 0);
    const executionLabel = getExecutionLabel(execution);
    const notification = {
      id: `${execution.id}-${Date.now()}`,
      executionId: execution.id,
      title: `${executionLabel} Execution Completed`,
      subtitle: execution.suiteName || executionLabel,
      summaryText: `${passed}/${total} passed`,
      status: failed > 0 ? 'failed' : 'passed',
      createdAt: new Date().toISOString(),
      read: false
    };

    setNotifications((prev) => [notification, ...prev].slice(0, MAX_NOTIFICATIONS));
  };

  const handleNotificationClick = (notification) => {
    setNotifications((prev) => prev.map((item) => (
      item.id === notification.id ? { ...item, read: true } : item
    )));
    setFocusedExecution({
      executionId: notification.executionId,
      sourceNotificationId: notification.id,
      triggeredAt: Date.now()
    });
    navigateToPage('results');
    setIsNotificationOpen(false);
  };

  const clearNotifications = () => {
    setNotifications([]);
    setIsNotificationOpen(false);
  };

  const openProfileModal = () => {
    setProfileError('');
    setProfileForm({
      name: currentUser?.name || '',
      email: currentUser?.email || '',
      currentPassword: '',
      newPassword: ''
    });
    setShowProfileModal(true);
    setIsProfileOpen(false);
  };

  const closeProfileModal = () => {
    if (profileSaving) return;
    setShowProfileModal(false);
    setProfileError('');
    setProfileForm({
      name: currentUser?.name || '',
      email: currentUser?.email || '',
      currentPassword: '',
      newPassword: ''
    });
  };

  const handleProfileSave = async (event) => {
    event.preventDefault();
    setProfileError('');

    const name = String(profileForm.name || '').trim();
    const email = String(profileForm.email || '').trim();

    if (!name) {
      setProfileError('Name is required.');
      return;
    }

    if (!email) {
      setProfileError('Email is required.');
      return;
    }

    const payload = { name, email };

    if (profileForm.currentPassword) {
      payload.currentPassword = profileForm.currentPassword;
    }

    if (profileForm.newPassword) {
      if (!profileForm.currentPassword) {
        setProfileError('Current password is required to set a new password.');
        return;
      }
      payload.newPassword = profileForm.newPassword;
    }

    setProfileSaving(true);
    try {
      const response = await axios.put('/api/auth/profile', payload);
      const updatedUser = response.data?.user;
      if (updatedUser) {
        setCurrentUser(updatedUser);
      }
      setShowProfileModal(false);
      setProfileForm((prev) => ({ ...prev, currentPassword: '', newPassword: '' }));
    } catch (error) {
      setProfileError(error.response?.data?.error || error.message || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const unreadNotificationCount = notifications.filter((notification) => !notification.read).length;

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return (
          <Dashboard 
            stats={stats} 
            onNavigate={navigateToPage} 
            host={host} 
            onHostChange={setHost}
          />
        );
      case 'apis':
        return <APIManagement onStats={fetchStats} host={host} />;
      case 'csv':
        return <CSVUpload onStats={fetchStats} />;
      case 'teststeps':
        return <TestSteps onStats={fetchStats} onExecutionComplete={handleExecutionComplete} />;
      case 'customFunctions':
        return (
          <CustomFunctions 
            openAiApiKey={openAiApiKey} 
            onApiKeysChange={(keys) => {
              setOpenAiApiKey(keys.openAiApiKey);
            }} 
          />
        );
      case 'extensions':
        return <DefaultExtensions />;
      case 'suites':
        return (
          <TestSuiteBuilder
            onStats={fetchStats}
            onExecutionComplete={handleExecutionComplete}
            onViewExecution={(executionId) => {
              setFocusedExecution({
                executionId,
                triggeredAt: Date.now()
              });
              navigateToPage('results');
            }}
          />
        );
      case 'results':
        return <ExecutionResults focusedExecution={focusedExecution} />;
      case 'about':
        return <About />;
      case 'contact':
        return <Contact user={currentUser} />;
      case 'files':
        return <FileAssets authToken={authToken} />;
      case 'addons':
        return <Addons />;

      default:
        return <Dashboard stats={stats} onNavigate={navigateToPage} host={host} onHostChange={setHost} />;
    }
  };

  if (!authToken || !currentUser) {
    return (
      <PublicSite
        page={publicPage}
        onNavigate={navigatePublicPage}
        authPanel={<AuthPage onAuthenticated={handleAuthenticated} compact />}
      />
    );
  }

  return (
    <div className="app">
      {isSidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />
      )}
      <aside className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="brand-block">
          <img src="/spiderman-logo-white.png" alt="Spider-Man" className="nav-logo-img" />
          <div className="brand-copy">
            <p>SPIDERWEBAUTOMATION</p>
            <strong>Control Panel</strong>
          </div>
        </div>

        <nav className="sidebar-nav">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => {
                navigateToPage(item.key);
                setIsSidebarOpen(false);
              }}
              className={`sidebar-link ${currentPage === item.key ? 'active' : ''}`}
            >
              <span className="sidebar-icon"><item.icon /></span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      <div className="workspace">
        <header className="topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button 
              type="button" 
              className="icon-btn mobile-only" 
              style={{ display: 'none' }} // Will be shown via CSS media query if needed, or I can just use className
              onClick={() => setIsSidebarOpen(true)}
            >
              <MdViewList />
            </button>
            <div>
              <p className="topbar-kicker">CSV-Driven API Automation</p>
              <h1 className="topbar-title">{PAGE_TITLES[currentPage] || PAGE_TITLES.dashboard}</h1>
            </div>
          </div>
          <div className="topbar-meta">
            <div className="status-chip">
              <MdApi />
              APIs
              <strong>{stats.apis}</strong>
            </div>
            <div className="status-chip">
              <MdStorage />
              Data
              <strong>{stats.csvs}</strong>
            </div>
            <div className="status-chip">
              <MdViewList />
              Suites
              <strong>{stats.suites}</strong>
            </div>
            <div className="topbar-pill">
              <MdCalendarToday />
              {new Date().toLocaleDateString()}
            </div>

            <div className="notification-wrapper" ref={notificationPanelRef}>
              <button
                type="button"
                className="icon-btn"
                aria-label="Notifications"
                onClick={() => {
                  setIsNotificationOpen((prev) => !prev);
                  setIsProfileOpen(false);
                }}
              >
                <MdNotificationsNone />
                {unreadNotificationCount > 0 && (
                  <span className="notification-badge">
                    {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                  </span>
                )}
              </button>

              {isNotificationOpen && (
                <div className="notification-panel">
                  <div className="notification-header">
                    <strong>Notifications</strong>
                    {notifications.length > 0 && (
                      <button type="button" className="notification-clear-btn" onClick={clearNotifications}>
                        Clear
                      </button>
                    )}
                  </div>

                  {notifications.length === 0 ? (
                    <p className="notification-empty">No notifications yet.</p>
                  ) : (
                    <div className="notification-list">
                      {notifications.map((notification) => (
                        <button
                          key={notification.id}
                          type="button"
                          className={`notification-item ${notification.read ? '' : 'unread'} ${notification.status}`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="notification-item-head">
                            <span>{notification.title}</span>
                            <small>{new Date(notification.createdAt).toLocaleString()}</small>
                          </div>
                          <p>{notification.subtitle}</p>
                          <small>{notification.summaryText}</small>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="profile-wrapper" ref={profilePanelRef}>
              <button
                type="button"
                className="profile-btn"
                onClick={() => {
                  setIsProfileOpen((prev) => !prev);
                  setIsNotificationOpen(false);
                }}
              >
                <MdAccountCircle />
                <span>{currentUser.name || currentUser.email}</span>
                <MdArrowDropDown />
              </button>

              {isProfileOpen && (
                <div className="profile-menu">
                  <button type="button" className="profile-menu-item" onClick={openProfileModal}>
                    <MdEdit />
                    Edit Profile
                  </button>
                  <button type="button" className="profile-menu-item" onClick={handleLogout}>
                    <MdLogout />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="main-content">
          {renderPage()}
        </main>
      </div>

      {showProfileModal && (
        <div className="modal-overlay" onClick={closeProfileModal}>
          <div className="modal-content modal-content-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Profile</h2>
              <button className="modal-close" onClick={closeProfileModal} disabled={profileSaving}>
                x
              </button>
            </div>
            <form onSubmit={handleProfileSave}>
              <div className="modal-body">
                {profileError && <div className="error" style={{ marginBottom: '12px' }}>{profileError}</div>}

                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, email: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Current Password (required for password change)</label>
                  <input
                    type="password"
                    value={profileForm.currentPassword}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
                    autoComplete="current-password"
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>New Password (optional)</label>
                  <input
                    type="password"
                    value={profileForm.newPassword}
                    onChange={(e) => setProfileForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-primary" onClick={closeProfileModal} disabled={profileSaving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-success" disabled={profileSaving}>
                  {profileSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
